import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import Navbar from './components/Navbar';
import BooksList from './components/BooksList';
import BookDetails from './components/BookDetails';
import AuthorsList from './components/AuthorsList';
import AuthorDetails from './components/AuthorDetails';
import AdminPanel from './components/admin/AdminPanel';
import AddBook from './components/admin/AddBook';
import EditBook from './components/admin/EditBook';
import AddAuthor from './components/admin/AddAuthor';
import EditAuthor from './components/admin/EditAuthor';
import ProtectedAdminRoute from './components/admin/ProtectedAdminRoute';
import { useEffect, useState } from 'react';
import Footer from './components/Footer';
import { Box } from '@mui/material';
import Users from './components/admin/Users';
import EditUser from './components/admin/EditUser';
import PrivacyPolicy from './components/PrivacyPolicy';
import DeleteAccount from './components/DeleteAccount';
import UserDetails from './components/user/UserDetails';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import EditProfile from './components/user/EditProfile';
import Settings from './components/user/Settings';

const kurdishFontStyle = `
  @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+Arabic:wght@400;500;700&display=swap');
`;

function App() {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'ku';
  });
  
  // Create rtl cache
  const cacheRtl = createCache({
    key: 'muirtl',
    stylisPlugins: [prefixer, rtlPlugin],
  });

  const theme = createTheme({
    direction: language === 'ku' ? 'rtl' : 'ltr',
    palette: {
      mode: 'light',
      primary: {
        main: '#1976d2',
      },
      secondary: {
        main: '#dc004e',
      },
      background: {
        default: '#f5f5f5',
        paper: '#ffffff',
      },
    },
    typography: {
      fontFamily: language === 'ku' 
        ? '"Noto Sans Arabic", "Arial Unicode MS", system-ui, sans-serif'
        : '"Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontSize: '2.5rem',
        fontWeight: 500,
      },
      h2: {
        fontSize: '2rem',
        fontWeight: 500,
      },
      h3: {
        fontSize: '1.75rem',
        fontWeight: 500,
      },
    },
    components: {
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            transition: 'transform 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-4px)',
            },
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            '&.book-title, &.author-name, &.genre-text': {
              direction: 'inherit',
              textAlign: 'inherit',
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            '&.genre-chip': {
              direction: 'inherit',
            },
          },
        },
      },
    },
  });

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    if (!savedLanguage) {
      localStorage.setItem('language', 'ku');
      setLanguage('ku');
    } else {
      setLanguage(savedLanguage);
    }
  }, []);

  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = language === 'ku' ? 'rtl' : 'ltr';
  }, [language]);

  return (
    <AuthProvider>
      <CacheProvider value={cacheRtl}>
        <style>{kurdishFontStyle}</style>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Router>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: '100vh',
              }}
            >
              <Navbar language={language} setLanguage={setLanguage} />
              <Box sx={{ flex: 1 }}>
                <Routes>
                  <Route path="/" element={<BooksList />} />
                  <Route path="/books/:id" element={<BookDetails />} />
                  <Route path="/books?user_id=:id" element={<BooksList />} />
                  <Route path="/authors" element={<AuthorsList />} />
                  <Route path="/authors/:id" element={<AuthorDetails />} />
                  <Route path="/users/:id" element={<UserDetails />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/delete-account" element={<DeleteAccount />} />
                  <Route 
                    path="/admin/*" 
                    element={
                      <ProtectedAdminRoute>
                        <AdminPanel />
                      </ProtectedAdminRoute>
                    } 
                  />
                  <Route path="/admin/books/add" element={<AddBook />} />
                  <Route path="/admin/books/edit/:id" element={<EditBook />} />
                  <Route path="/admin/authors" element={<AuthorsList />} />
                  <Route path="/admin/authors/add" element={<AddAuthor />} />
                  <Route path="/admin/authors/edit/:id" element={<EditAuthor />} />
                  <Route path="/admin/users" element={<Users />} />
                  <Route path="/admin/users/edit/:id" element={<EditUser />} />
                  <Route path="/profile" element={
                    <ProtectedRoute>
                      <EditProfile />
                    </ProtectedRoute>
                  } />
                  <Route path="/settings" element={
                    <ProtectedRoute>
                      <Settings />
                    </ProtectedRoute>
                  } />
                </Routes>
              </Box>
              <Footer language={language} />
            </Box>
          </Router>
        </ThemeProvider>
      </CacheProvider>
    </AuthProvider>
  );
}

export default App;
